import ky from "ky"
import { z } from "zod"
import { isNativeApp, requestNativeCapability } from "./native-capabilities.ts"
import { logoutNativeWebSession } from "./native-session-client.ts"
import type { UserRoute } from "./routes.ts"
import { phoneVerificationStatusSchema } from "./todo26-contract.ts"
import { type UserView, userViewSchema } from "./user-view.ts"

const staticCommandResponseSchema = z.strictObject({ kind: z.literal("static"), nextRoute: z.string().startsWith("/") })
const resourceCommandResponseSchema = z.strictObject({ kind: z.literal("resource"), nextRoute: z.string().startsWith("/"), resourceId: z.uuid() })
export const commandResponseSchema = z.discriminatedUnion("kind", [staticCommandResponseSchema, resourceCommandResponseSchema])
export type CommandResponse = z.infer<typeof commandResponseSchema>
const commandErrorResponseSchema = z.object({
  balanceWon: z.coerce.number().int().nonnegative().optional(),
  code: z.string().min(1),
  deletionRequestId: z.uuid().optional(),
  pendingTransactionCount: z.coerce.number().int().nonnegative().optional(),
})
export type CommandErrorResponse = z.infer<typeof commandErrorResponseSchema>
const apiBase = process.env["NEXT_PUBLIC_API_BASE_URL"] ?? "/api"
const apiUrl = (endpoint: string): string => `${apiBase}${endpoint}`
let refreshSessionPromise: Promise<boolean> | null = null

const refreshSession = (): Promise<boolean> => {
  if (refreshSessionPromise !== null) return refreshSessionPromise
  const refresh = ky
    .post(apiUrl("/identity/refresh"), { credentials: "include", json: { client: "WEB" }, retry: 0, throwHttpErrors: false, timeout: 10_000 })
    .then((response) => response.ok)
    .catch(() => false)
  refreshSessionPromise = refresh.finally(() => {
    refreshSessionPromise = null
  })
  return refreshSessionPromise
}

const pathValue = (pathname: string): string => pathname.split("/").at(-1) ?? ""

export const viewEndpoint = (route: UserRoute, pathname: string, search: URLSearchParams): string => {
  const scenario = search.get("scenario")
  const scenarioQuery = scenario === null ? "" : `scenario=${encodeURIComponent(scenario)}`
  let endpoint: string
  switch (route.id) {
    case "APP-ENTRY-001":
      endpoint = `/entry?entry=${encodeURIComponent(search.get("entry") ?? "")}`
      break
    case "APP-HOME-001":
    case "APP-BAL-001":
      endpoint = `/web/views?route=${encodeURIComponent(pathname)}`
      break
    case "APP-BAL-002":
      endpoint = `/web/views?route=${encodeURIComponent(pathname)}`
      break
    case "APP-BAL-003":
      endpoint = `/web/views?route=${encodeURIComponent(pathname)}`
      break
    case "APP-SHOP-001":
      endpoint = `/web/views?route=${encodeURIComponent(pathname)}`
      break
    case "APP-SHOP-002":
      endpoint = `/web/views?route=${encodeURIComponent("/shop")}`
      break
    case "APP-SHOP-003":
      endpoint = `/products/${pathValue(pathname)}`
      break
    case "APP-VCH-002":
      endpoint = `/redemptions/${pathValue(pathname)}`
      break
    case "APP-VERIFY-001":
      endpoint = "/mobile/identity/phone"
      break
    default:
      endpoint = `/web/views?route=${encodeURIComponent(pathname)}`
  }
  if (scenarioQuery.length === 0) return endpoint
  return `${endpoint}${endpoint.includes("?") ? "&" : "?"}${scenarioQuery}`
}

export const parseUserViewResponse = (route: UserRoute, response: unknown): UserView => {
  try {
    return parseUserView(route, response)
  } catch (error: unknown) {
    if (error instanceof z.ZodError) throw new UserViewRequestError(200)
    throw error
  }
}

const parseUserView = (route: UserRoute, response: unknown): UserView => {
  if (route.id !== "APP-VERIFY-001") return userViewSchema.parse(response)
  const phoneVerification = phoneVerificationStatusSchema.parse(response)
  return userViewSchema.parse({
    description: "상품권을 받을 휴대전화 상태를 확인하세요.",
    details: [],
    heading: "휴대전화 관리",
    items: [],
    kind: "page",
    phoneVerification,
    state: "DEFAULT",
  })
}

export const loadUserView = async (route: UserRoute, pathname: string, search: URLSearchParams, signal: AbortSignal): Promise<UserView> => {
  const endpoint = apiUrl(viewEndpoint(route, pathname, search))
  let request = await ky.get(endpoint, { credentials: "include", retry: 0, signal, throwHttpErrors: false, timeout: 10_000 })
  if (request.status === 401 && (await refreshSession())) request = await ky.get(endpoint, { credentials: "include", retry: 0, signal, throwHttpErrors: false, timeout: 10_000 })
  if (request.status === 401) throw new AuthenticationRequiredError()
  if (!request.ok) throw new UserViewRequestError(request.status)
  const response: unknown = await request.json().catch((error: unknown) => {
    if (error instanceof SyntaxError) throw new UserViewRequestError(request.status)
    throw error
  })
  return parseUserViewResponse(route, response)
}

export const submitCommand = async (endpoint: string, body: Readonly<Record<string, string | boolean>>, idempotencyKey?: string): Promise<CommandResponse> => {
  const options = idempotencyKey === undefined ? { credentials: "include" as const, json: body, retry: 0 as const, throwHttpErrors: false, timeout: 10_000 } : { credentials: "include" as const, headers: { "idempotency-key": idempotencyKey }, json: body, retry: 0 as const, throwHttpErrors: false, timeout: 10_000 }
  let request = await ky.post(apiUrl(endpoint), options)
  if (request.status === 401 && (await refreshSession())) request = await ky.post(apiUrl(endpoint), options)
  const response: unknown = await request.json().catch(() => null)
  if (!request.ok) {
    const error = commandErrorResponseSchema.safeParse(response)
    throw new CommandRequestError(request.status, error.success ? error.data : { code: "COMMAND_FAILED" })
  }
  return commandResponseSchema.parse(response)
}

export type LogoutResult = "logged-out" | "failed"

export const logoutUser = async (): Promise<LogoutResult> => {
  try {
    if (isNativeApp()) {
      window.dispatchEvent(new CustomEvent("cashhub:session-clearing", { detail: { reason: "logout" } }))
      if (!(await logoutNativeWebSession())) {
        window.dispatchEvent(new CustomEvent("cashhub:session-ready", { detail: { reason: "logout-failed" } }))
        return "failed"
      }
      // Server revocation removes this session's push devices atomically. SDK failure cannot block logout.
      await requestNativeCapability("signOut").catch(() => undefined)
      return "logged-out"
    }
    const response = await ky.post(apiUrl("/identity/logout"), { credentials: "include", retry: 0, throwHttpErrors: false, timeout: 10_000 })
    return response.status === 204 ? "logged-out" : "failed"
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (isNativeApp()) window.dispatchEvent(new CustomEvent("cashhub:session-ready", { detail: { reason: "logout-failed" } }))
      return "failed"
    }
    throw error
  }
}

export class AuthenticationRequiredError extends Error {
  override readonly name = "AuthenticationRequiredError"
  constructor() {
    super("T05_SESSION_REQUIRED")
  }
}

export class UserViewRequestError extends Error {
  override readonly name = "UserViewRequestError"
  constructor(readonly status: number) {
    super("T05_WEB_VIEW_FAILED")
  }
}

export class CommandRequestError extends Error {
  override readonly name = "CommandRequestError"
  constructor(
    readonly status: number,
    readonly details: CommandErrorResponse,
  ) {
    super(details.code)
  }
}
